<template>
  <ComponentHeader title="Clinical Summaries" subtitle="Configure AI-powered clinical summary templates for Rx and Dx data. Define the LLM prompts and output schemas for generating intelligent summaries.">
    <button
      @click="createNewTemplate"
      class="btn-primary-sm"
    >
      <FontAwesomeIcon :icon="['fas', 'plus']" />
      Create Template
    </button>
  </ComponentHeader>

  <div class="clinical-summary-template-admin p-4">
    <!-- Loading State -->
    <div v-if="loading" class="py-12 text-center">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-primary-600 text-3xl animate-spin" />
    </div>

    <!-- Empty State -->
    <div v-else-if="templates.length === 0" class="py-12 text-center bg-gray-50 rounded-lg">
      <FontAwesomeIcon :icon="['fas', 'file-lines']" class="text-gray-400 text-5xl mb-3" />
      <p class="text-gray-600 mb-2">No clinical summary templates configured</p>
      <p class="text-sm text-gray-500 mb-4">Create templates to enable AI-powered clinical summaries</p>
      <button
        @click="createNewTemplate"
        class="btn-primary-sm"
      >
        <FontAwesomeIcon :icon="['fas', 'plus']" />
        Create Your First Template
      </button>
    </div>

    <!-- Templates List -->
    <div v-else class="space-y-4">
      <!-- Template Card -->
      <div
        v-for="template in templates"
        :key="template.id"
        class="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <!-- Template Header -->
        <div class="px-6 py-4 border-b border-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center"
                :class="getTypeColor(template.metadata.template_type)"
              >
                <FontAwesomeIcon :icon="getTypeIcon(template.metadata.template_type)" class="text-lg" />
              </div>
              <div>
                <h5 class="font-semibold text-gray-900">{{ template.name }}</h5>
                <p class="text-sm text-gray-500">{{ template.description || 'No description' }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <!-- Type Badge -->
              <span
                class="px-3 py-1 text-xs font-medium rounded-full"
                :class="getTypeBadgeClass(template.metadata.template_type)"
              >
                {{ formatTemplateType(template.metadata.template_type) }}
              </span>

              <!-- Edit Button -->
              <button
                @click="editTemplate(template)"
                class="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Edit Template"
              >
                <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
              </button>

              <!-- Delete Button -->
              <button
                @click="deleteTemplate(template)"
                class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Template"
              >
                <FontAwesomeIcon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
        </div>

        <!-- Template Details -->
        <div class="px-6 py-4 bg-gray-50">
          <div class="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Data Function:</span>
              <span class="ml-2 font-mono text-xs text-gray-900">{{ template.metadata.data_function }}</span>
            </div>
            <div>
              <span class="text-gray-500">Date Field:</span>
              <span class="ml-2 font-mono text-xs text-gray-900">{{ template.metadata.date_field_for_sorting }}</span>
            </div>
            <div>
              <span class="text-gray-500">Default Limit:</span>
              <span class="ml-2 font-medium text-gray-900">{{ template.metadata.limit_to_recent || 20 }} records</span>
            </div>
          </div>

          <!-- Prompt Preview -->
          <div class="mt-3 pt-3 border-t border-gray-200">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <span class="text-xs font-medium text-gray-500 uppercase">Prompt Preview:</span>
                <p class="text-xs text-gray-700 mt-1 line-clamp-2">{{ template.metadata.prompt }}</p>
              </div>
              <button
                @click="togglePromptExpand(template.id)"
                class="ml-4 text-primary-600 hover:text-primary-700 text-xs"
              >
                {{ expandedPrompts[template.id] ? 'Show Less' : 'Show More' }}
              </button>
            </div>
            <div v-if="expandedPrompts[template.id]" class="mt-2">
              <pre class="text-xs text-gray-700 whitespace-pre-wrap bg-white rounded border border-gray-200 p-3">{{ template.metadata.prompt }}</pre>
            </div>
          </div>

          <!-- Schema Info -->
          <div class="mt-3 pt-3 border-t border-gray-200">
            <div class="flex items-center justify-between">
              <div>
                <span class="text-xs font-medium text-gray-500 uppercase">Output Schema:</span>
                <span class="ml-2 text-xs text-gray-700">
                  {{ Object.keys(template.metadata.output_schema?.properties || {}).length }} fields defined
                </span>
              </div>
              <button
                @click="toggleSchemaExpand(template.id)"
                class="text-primary-600 hover:text-primary-700 text-xs"
              >
                {{ expandedSchemas[template.id] ? 'Hide Schema' : 'View Schema' }}
              </button>
            </div>
            <div v-if="expandedSchemas[template.id]" class="mt-2">
              <pre class="text-xs text-gray-700 whitespace-pre-wrap bg-white rounded border border-gray-200 p-3 max-h-64 overflow-y-auto">{{ JSON.stringify(template.metadata.output_schema, null, 2) }}</pre>
            </div>
          </div>

          <!-- Timestamps -->
          <div class="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <span>Created: {{ formatDate(template.created_at) }}</span>
            <span>Updated: {{ formatDate(template.updated_at) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Editor Modal -->
    <TemplateEditorModal
      v-if="showEditorModal"
      :template="editingTemplate"
      :is-new="isNewTemplate"
      @save="saveTemplate"
      @close="closeEditorModal"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TemplateEditorModal from './ClinicalSummaryTemplate_EditorModal.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default {
  name: 'ClinicalSummaryTemplate_Admin',

  components: {
    FontAwesomeIcon,
    TemplateEditorModal,
    ComponentHeader
  },

  data() {
    return {
      templates: [],
      loading: true,
      showEditorModal: false,
      editingTemplate: null,
      isNewTemplate: false,
      expandedPrompts: {},
      expandedSchemas: {}
    };
  },

  mounted() {
    this.fetchTemplates();
  },

  methods: {
    async fetchTemplates() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/api/patientclinicalsummarytemplates/');
        this.templates = response.data.sort((a, b) => {
          // Sort by template_type (dx, rx)
          const typeOrder = { dx: 1, rx: 2, lab: 3 };
          return (typeOrder[a.metadata.template_type] || 999) - (typeOrder[b.metadata.template_type] || 999);
        });
      } catch (error) {
        console.error('Error fetching templates:', error);
        this.$emit('show-message', 'Failed to load templates', 'error');
      } finally {
        this.loading = false;
      }
    },

    createNewTemplate() {
      this.editingTemplate = {
        name: '',
        description: '',
        metadata: {
          template_type: 'dx',
          data_function: 'return_patient_detail_dx',
          date_field_for_sorting: 'last_billed',
          limit_to_recent: 20,
          prompt: '',
          output_schema: {
            type: 'object',
            properties: {},
            required: []
          }
        }
      };
      this.isNewTemplate = true;
      this.showEditorModal = true;
    },

    editTemplate(template) {
      // Deep clone to avoid mutating original
      this.editingTemplate = JSON.parse(JSON.stringify(template));
      this.isNewTemplate = false;
      this.showEditorModal = true;
    },

    async deleteTemplate(template) {
      if (!confirm(`Are you sure you want to delete "${template.name}"? This cannot be undone.`)) {
        return;
      }

      try {
        await this.$axios.delete(`/api/patientclinicalsummarytemplates/${template.id}/`);
        this.$emit('show-message', 'Template deleted successfully', 'success');
        this.fetchTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        this.$emit('show-message', 'Failed to delete template', 'error');
      }
    },

    async saveTemplate(templateData) {
      try {
        if (this.isNewTemplate) {
          await this.$axios.post('/api/patientclinicalsummarytemplates/', templateData);
          this.$emit('show-message', 'Template created successfully', 'success');
        } else {
          await this.$axios.patch(`/api/patientclinicalsummarytemplates/${templateData.id}/`, templateData);
          this.$emit('show-message', 'Template updated successfully', 'success');
        }

        this.closeEditorModal();
        this.fetchTemplates();
      } catch (error) {
        console.error('Error saving template:', error);
        this.$emit('show-message', 'Failed to save template', 'error');
      }
    },

    closeEditorModal() {
      this.showEditorModal = false;
      this.editingTemplate = null;
      this.isNewTemplate = false;
    },

    togglePromptExpand(templateId) {
      this.expandedPrompts[templateId] = !this.expandedPrompts[templateId];
    },

    toggleSchemaExpand(templateId) {
      this.expandedSchemas[templateId] = !this.expandedSchemas[templateId];
    },

    getTypeIcon(type) {
      const icons = {
        dx: 'file-medical',
        rx: 'pills',
        lab: 'flask'
      };
      return ['fas', icons[type] || 'file-lines'];
    },

    getTypeColor(type) {
      const colors = {
        dx: 'bg-red-100 text-red-600',
        rx: 'bg-green-100 text-green-600',
        lab: 'bg-blue-100 text-blue-600'
      };
      return colors[type] || 'bg-gray-100 text-gray-600';
    },

    getTypeBadgeClass(type) {
      const classes = {
        dx: 'bg-red-100 text-red-700',
        rx: 'bg-green-100 text-green-700',
        lab: 'bg-blue-100 text-blue-700'
      };
      return classes[type] || 'bg-gray-100 text-gray-700';
    },

    formatTemplateType(type) {
      const labels = {
        dx: 'Diagnosis',
        rx: 'Prescription',
        lab: 'Laboratory'
      };
      return labels[type] || type.toUpperCase();
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
